import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import ImageGallery from '../components/ImageGallery';
import Testimonials from '../components/Testimonials';
import NewsEvents from '../components/NewsEvents';
import './Home.css';

const heroSlides = [
  {
    title: "Building A Solid Foundation",
    subtitle: "Empowering Future Leaders Through Excellence",
    description: "Where tradition meets innovation in education"
  },
  {
    title: "Excellence in Education",
    subtitle: "Nurturing Minds, Shaping Futures",
    description: "A legacy of academic achievement and character development"
  },
  {
    title: "Join Our Community",
    subtitle: "Where Every Student Matters",
    description: "Creating tomorrow's changemakers today"
  }
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="home">
      {/* Hero Section with Slideshow */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-slideshow">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{backgroundImage: `url('/assets/images/hero-bg-${index + 1}.jpg')`}}
            ></div>
          ))}
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-logo-badge">
              <Logo width={120} height={120} className="hero-logo-svg" />
            </div>
            <h1 className="hero-title fade-in">
              {heroSlides[currentSlide].title}
            </h1>
            <p className="hero-subtitle">
              {heroSlides[currentSlide].subtitle}
            </p>
            <p className="hero-description">
              {heroSlides[currentSlide].description}
            </p>
            <div className="hero-buttons">
              <Link to="/admissions" className="btn btn-primary">
                Apply for Admission
              </Link>
              <Link to="/contact" className="btn btn-secondary-outline">
                Book a Visit
              </Link>
            </div>
          </div>
        </div>
        <div className="hero-scroll-indicator">
          <span></span>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">15+</div>
              <div className="stat-label">Years of Excellence</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Students Enrolled</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">50+</div>
              <div className="stat-label">Qualified Teachers</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section with Image */}
      <section className="welcome-section">
        <div className="container">
          <div className="welcome-grid">
            <div className="welcome-image">
              <img src="/assets/images/principal.jpg" alt="Principal" />
              <div className="welcome-image-badge">
                <span>Principal's Message</span>
              </div>
            </div>
            <div className="welcome-content">
              <h2 className="section-title-left">Welcome to The MINT School</h2>
              <p className="welcome-lead">
                It is with great pleasure that I welcome you to The MINT School,
                Asa-Otukpo, Benue State's premier institution for holistic education.
              </p>
              <p>
                At MINT, we believe that every child deserves an education that not
                only equips them with academic knowledge but also builds character,
                resilience, and a love for lifelong learning. Our dedicated team of
                educators works tirelessly to create an environment where students
                can thrive, explore their passions, and reach their full potential.
              </p>
              <p>
                We invite you to explore our website and discover what makes MINT
                School the right choice for your child's educational journey.
              </p>
              <div className="signature">
                <strong>Dr. [Principal Name]</strong>
                <span>Principal, The MINT School</span>
              </div>
              <Link to="/about" className="btn btn-primary-outline">
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Why Choose The MINT School?</h2>
          <p className="section-subtitle">Excellence in education, character development, and student success</p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 14L21 9L12 4L3 9L12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 14L12 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 11V16C19 16 19 19 12 19C5 19 5 16 5 16V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Academic Excellence</h3>
              <p>Rigorous curriculum aligned with Nigerian and international standards, preparing students for success in higher education and beyond.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Expert Faculty</h3>
              <p>Passionate and highly qualified educators committed to inspiring students and fostering a love for learning.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Modern Facilities</h3>
              <p>State-of-the-art classrooms, science laboratories, library, sports facilities, and technology-enhanced learning spaces.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Holistic Development</h3>
              <p>Comprehensive approach to education including sports, arts, leadership training, and character development programs.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Global Perspective</h3>
              <p>Preparing students to thrive in a globalized world through international curriculum and cultural awareness programs.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Small Class Sizes</h3>
              <p>Personalized attention and individualized learning support to ensure every student reaches their full potential.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="programs">
        <div className="container">
          <h2 className="section-title">Our Academic Programs</h2>
          <div className="programs-grid">
            <div className="program-card">
              <h3>Primary Education</h3>
              <p>Foundation years focusing on basic literacy, numeracy, and social skills development.</p>
              <ul>
                <li>Nursery & Kindergarten</li>
                <li>Primary 1-6</li>
                <li>Age-appropriate curriculum</li>
              </ul>
            </div>
            <div className="program-card">
              <h3>Secondary Education</h3>
              <p>Comprehensive secondary education preparing students for higher education and career paths.</p>
              <ul>
                <li>Junior Secondary (JSS 1-3)</li>
                <li>Senior Secondary (SSS 1-3)</li>
                <li>Science & Arts streams</li>
              </ul>
            </div>
            <div className="program-card">
              <h3>Extracurricular Activities</h3>
              <p>Beyond academics, we offer various activities to develop students' talents and interests.</p>
              <ul>
                <li>Sports & Athletics</li>
                <li>Music & Arts</li>
                <li>Debate & Public Speaking</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <ImageGallery
        title="Life at MINT School"
        subtitle="Experience the vibrant learning environment and diverse activities at our campus"
      />

      {/* Testimonials */}
      <Testimonials
        title="What Our Community Says"
        subtitle="Hear from parents, students, and alumni about their MINT School experience"
      />

      {/* News & Events */}
      <NewsEvents
        title="Latest News & Upcoming Events"
        subtitle="Stay updated with the latest happenings at MINT School"
      />

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Join Our Community?</h2>
            <p>Take the first step towards your child's bright future. Contact us today to learn more about admissions and enrollment.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Contact Us
              </Link>
              <Link to="/admissions" className="btn btn-outline">
                View Admissions
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
